import { useState, useEffect, useRef } from 'react';
import apiService from '../services/apiService';
import logger from '../utils/logger';

/**
 * Fetches and manages comps data for a unit.
 * Loads eagerly on unit open so AI Insights can compute peer summaries.
 * Uses AbortController to cancel in-flight requests when the unit changes
 * or the component unmounts.
 */
export function useUnitComps(unit) {
    const [comps, setComps] = useState(null);
    const [compsLoading, setCompsLoading] = useState(false);
    const [compsFetched, setCompsFetched] = useState(false);
    const abortRef = useRef(null);

    // Reset state whenever the unit changes
    useEffect(() => {
        if (abortRef.current) abortRef.current.abort();
        setComps(null);
        setCompsFetched(false);
        setCompsLoading(false);
    }, [unit.StockNumber]);

    // Fetch eagerly on unit open
    useEffect(() => {
        if (compsFetched) return;

        const controller = new AbortController();
        abortRef.current = controller;
        setCompsLoading(true);

        apiService.getUnitComps(
            unit.StockNumber, unit.MAKE, unit.MODEL, unit.YEAR_MANUF,
            { signal: controller.signal }
        )
            .then(data => {
                if (!controller.signal.aborted) {
                    setComps(data);
                    setCompsFetched(true);
                }
            })
            .catch(err => {
                if (!controller.signal.aborted) {
                    logger.error('Comps fetch failed', err);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setCompsLoading(false);
            });

        return () => controller.abort();
    }, [compsFetched, unit.StockNumber, unit.MAKE, unit.MODEL, unit.YEAR_MANUF]);

    return { comps, compsLoading };
}
